/**
 * Google Analytics 4 Event Tracking
 * Uses gtag.js directly for GA4
 */

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// GA4 Measurement ID
const GA4_MEASUREMENT_ID = 'G-224M982M93';

// Current session ID for tracking
let currentSessionId: string | null = null;

/**
 * Simple hash function for email -> user_id
 */
const hashEmail = async (email: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 32);
};

/**
 * Set user ID in GA (hashed email)
 */
export const setUserId = async (email: string) => {
  if (typeof window !== 'undefined' && window.gtag && email) {
    const hashedId = await hashEmail(email);
    window.gtag('config', GA4_MEASUREMENT_ID, {
      user_id: hashedId,
    });
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Set user_id:', hashedId);
    }
  }
};

/**
 * Set current session ID for tracking
 */
export const setSessionId = (sessionId: string) => {
  currentSessionId = sessionId;
};

/**
 * Track event using gtag.js
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Use assessment_session_id to avoid conflict with GA4 reserved session_id
    const params: Record<string, any> = {
      ...eventParams,
      send_to: GA4_MEASUREMENT_ID,
    };

    // Add assessment session ID if available
    if (currentSessionId) {
      params.assessment_session_id = currentSessionId;
    }

    window.gtag('event', eventName, params);

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics]', eventName, params);
    }
  }
};

// ============================================================================
// Assessment Events
// ============================================================================

/**
 * Track when user starts the assessment (clicks start button on homepage)
 */
export const trackAssessmentStart = (assessmentType: string, language: string) => {
  trackEvent('assessment_start', {
    assessment_type: assessmentType,
    language,
  });
};

/**
 * Track when anonymous session is created
 */
export const trackSessionCreated = (sessionId: string, assessmentType: string) => {
  trackEvent('session_created', {
    assessment_type: assessmentType,
    // assessment_session_id is added automatically by trackEvent
  });
};

/**
 * Track question answered (for milestones: 25%, 50%, 75%, 100%)
 */
export const trackQuestionAnswered = (
  questionIndex: number,
  totalQuestions: number,
  assessmentType: string
) => {
  const progress = Math.round(((questionIndex + 1) / totalQuestions) * 100);

  // Track milestones: first question, 25%, 50%, 75%, last question
  const milestones = [1, 25, 50, 75, 100];
  const currentMilestone = milestones.find(m => {
    const milestoneIndex = Math.floor((m / 100) * totalQuestions);
    return questionIndex + 1 === milestoneIndex || (m === 1 && questionIndex === 0);
  });

  if (currentMilestone || questionIndex === 0 || questionIndex === totalQuestions - 1) {
    trackEvent('question_milestone', {
      question_number: questionIndex + 1,
      total_questions: totalQuestions,
      progress_percent: progress,
      milestone: currentMilestone || progress,
      assessment_type: assessmentType,
    });
  }
};

/**
 * Track when all questions are answered (before participant form)
 */
export const trackQuestionnaireComplete = (
  totalQuestions: number,
  assessmentType: string
) => {
  trackEvent('questionnaire_complete', {
    total_questions: totalQuestions,
    assessment_type: assessmentType,
  });
};

/**
 * Track when participant form is shown
 */
export const trackParticipantFormView = () => {
  trackEvent('participant_form_view');
};

/**
 * Track when participant submits their info
 */
export const trackParticipantFormSubmit = (
  hasPhone: boolean,
  hasAddress: boolean,
  position: string
) => {
  trackEvent('participant_form_submit', {
    has_phone: hasPhone,
    has_address: hasAddress,
    position,
  });
};

/**
 * Track final assessment submission
 */
export const trackAssessmentSubmit = (
  sessionId: string,
  assessmentType: string,
  totalQuestions: number
) => {
  trackEvent('assessment_submit', {
    session_id: sessionId,
    assessment_type: assessmentType,
    total_questions: totalQuestions,
  });
};

/**
 * Track assessment completion (results page view)
 */
export const trackAssessmentComplete = (
  sessionId: string,
  assessmentType: string
) => {
  trackEvent('assessment_complete', {
    session_id: sessionId,
    assessment_type: assessmentType,
  });
};

/**
 * Track homepage view
 */
export const trackHomepageView = (language: string) => {
  trackEvent('homepage_view', {
    language,
  });
};

/**
 * Track CTA button click
 */
export const trackCTAClick = (buttonLocation: string, language: string) => {
  trackEvent('cta_click', {
    button_location: buttonLocation,
    language,
  });
};

/**
 * Track campaign source from email (Brevo)
 */
export const trackCampaignSource = async (params: {
  email?: string;
  campaign?: string;
  source?: string;
  medium?: string;
}) => {
  // Set user_id if email exists
  if (params.email) {
    await setUserId(params.email);
  }

  trackEvent('campaign_visit', {
    has_email: !!params.email,
    campaign: params.campaign || 'direct',
    source: params.source || 'direct',
    medium: params.medium || 'none',
  });

  // Also set user properties for attribution
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', {
      campaign_source: params.source || 'direct',
      campaign_name: params.campaign || 'direct',
      from_email_campaign: !!params.email,
    });
  }
};

// ============================================================================
// Quiz Events — shared by the lead qualifier (/guide) and the readiness
// campaign (/ready)
// ============================================================================

/**
 * Which quiz the visitor is currently in.
 *
 * Both quizzes emit the same event names, so without this every funnel in GA4
 * is the sum of two different products and no drop-off number means anything.
 * Each quiz page sets it once on mount; every quiz event below carries it.
 */
let currentQuizType = 'lead_qualifier';
export const setQuizType = (quizType: string) => {
  currentQuizType = quizType;
};

// Stamps quiz_type onto every quiz event, so a new event can never be added
// without it.
const trackQuizEvent = (eventName: string, params: Record<string, any>) => {
  trackEvent(eventName, { ...params, quiz_type: currentQuizType });
};

/**
 * Track quiz landing page view
 */
export const trackQuizLandingView = (language: string) => {
  trackQuizEvent('quiz_landing_view', { language });
};

/**
 * Track quiz start (click start button)
 */
export const trackQuizStart = (language: string) => {
  trackQuizEvent('quiz_start', { language });
};

/**
 * Track quiz question view
 */
export const trackQuizQuestionView = (
  questionNumber: string,
  questionId: string,
  questionType: string,
  totalQuestions: number,
  questionIndex: number,
  language: string
) => {
  trackQuizEvent('quiz_question_view', {
    question_number: questionNumber,
    question_id: questionId,
    question_type: questionType,
    total_questions: totalQuestions,
    question_index: questionIndex,
    progress_percent: Math.round(((questionIndex + 1) / totalQuestions) * 100),
    language,
  });
};

/**
 * Track quiz question answered
 */
export const trackQuizQuestionAnswered = (
  questionNumber: string,
  questionId: string,
  questionType: string,
  answerValue: string | string[],
  totalQuestions: number,
  questionIndex: number,
  language: string,
  // Anything the individual quiz knows and the shared shape does not — how long
  // the question took, whether the answer replaced an earlier one, and so on.
  extra: Record<string, any> = {}
) => {
  const answerCount = Array.isArray(answerValue) ? answerValue.length : 1;
  const hasOther = Array.isArray(answerValue)
    ? answerValue.some(v => v.startsWith('other:'))
    : answerValue.startsWith('other:');

  trackQuizEvent('quiz_question_answered', {
    question_number: questionNumber,
    question_id: questionId,
    question_type: questionType,
    // The value itself, not only its shape: without it there is no way to see
    // which answer precedes a drop-off.
    answer_value: Array.isArray(answerValue) ? answerValue.join(',') : answerValue,
    answer_count: answerCount,
    has_other: hasOther,
    question_index: questionIndex,
    progress_percent: Math.round(((questionIndex + 1) / totalQuestions) * 100),
    language,
    ...extra,
  });
};

/**
 * Track quiz progress milestones (25%, 50%, 75%, 100%)
 */
export const trackQuizMilestone = (
  milestone: number,
  totalQuestions: number,
  language: string
) => {
  trackQuizEvent('quiz_milestone', {
    milestone_percent: milestone,
    total_questions: totalQuestions,
    language,
  });
};

/**
 * Track quiz contact form view
 */
export const trackQuizContactFormView = (language: string) => {
  trackQuizEvent('quiz_contact_form_view', { language });
};

/**
 * Track quiz contact form field focus
 */
export const trackQuizFormFieldFocus = (fieldName: string, language: string) => {
  trackQuizEvent('quiz_form_field_focus', {
    field_name: fieldName,
    language,
  });
};

/**
 * Track privacy policy modal opened
 */
export const trackQuizPrivacyPolicyView = (language: string) => {
  trackQuizEvent('quiz_privacy_policy_view', {
    language,
  });
};

/**
 * Track privacy consent toggle
 */
export const trackQuizPrivacyConsent = (consented: boolean, language: string) => {
  trackQuizEvent('quiz_privacy_consent', {
    consented,
    language,
  });
};

/**
 * Track quiz form submit attempt
 */
export const trackQuizFormSubmitAttempt = (
  hasAllFields: boolean,
  hasPrivacyConsent: boolean,
  language: string
) => {
  trackQuizEvent('quiz_form_submit_attempt', {
    has_all_fields: hasAllFields,
    has_privacy_consent: hasPrivacyConsent,
    language,
  });
};

/**
 * Track quiz form validation error
 */
export const trackQuizFormError = (errorType: string, language: string) => {
  trackQuizEvent('quiz_form_error', {
    error_type: errorType,
    language,
  });
};

/**
 * Track quiz completion (successful submit)
 */
export const trackQuizComplete = (
  totalQuestions: number,
  answeredQuestions: number,
  language: string,
  extra: Record<string, any> = {}
) => {
  trackQuizEvent('quiz_complete', {
    total_questions: totalQuestions,
    answered_questions: answeredQuestions,
    completion_rate: Math.round((answeredQuestions / totalQuestions) * 100),
    language,
    ...extra,
  });
};

/**
 * Track gift download click
 */
export const trackQuizGiftDownload = (language: string) => {
  trackQuizEvent('quiz_gift_download', {
    language,
    gift_type: 'productivity_guide_pdf',
  });
};

/**
 * Track start 360 assessment from quiz
 */
export const trackQuizStart360 = (language: string, deepAnalysisAnswer: string) => {
  trackQuizEvent('quiz_start_360', {
    language,
    deep_analysis_answer: deepAnalysisAnswer,
    source: 'quiz_completion',
  });
};

/**
 * Track quiz finish button click
 */
export const trackQuizFinish = (language: string) => {
  trackQuizEvent('quiz_finish', {
    language,
  });
};

/**
 * Track quiz language toggle
 */
export const trackQuizLanguageToggle = (fromLanguage: string, toLanguage: string) => {
  trackQuizEvent('quiz_language_toggle', {
    from_language: fromLanguage,
    to_language: toLanguage,
  });
};

/**
 * Track quiz back button click
 */
export const trackQuizBackButton = (
  fromStep: number | string,
  toStep: number | string,
  language: string
) => {
  trackQuizEvent('quiz_back_button', {
    from_step: String(fromStep),
    to_step: String(toStep),
    language,
  });
};

/**
 * Track a forward move made by pressing the next button rather than by
 * picking an answer. The counterpart to quiz_back_button: together they show
 * who is navigating back and forth instead of moving straight through.
 */
export const trackQuizNextButton = (
  fromStep: number | string,
  toStep: number | string,
  language: string
) => {
  trackQuizEvent('quiz_next_button', {
    from_step: String(fromStep),
    to_step: String(toStep),
    language,
  });
};

/**
 * Track a contact form field being filled in — fired on blur, once per field.
 *
 * quiz_form_field_focus says which field they reached; this says which one
 * they actually finished, and the gap between the two is where the form loses
 * people.
 */
export const trackQuizFormFieldComplete = (fieldName: string, language: string) => {
  trackQuizEvent('quiz_form_field_complete', {
    field_name: fieldName,
    language,
  });
};

/**
 * Track the privacy policy modal being closed, with how long it was open.
 */
export const trackQuizPrivacyPolicyClose = (msOpen: number, language: string) => {
  trackQuizEvent('quiz_privacy_policy_close', {
    time_open_ms: msOpen,
    language,
  });
};

/**
 * Track leaving the quiz without finishing it.
 *
 * Fired on pagehide, which is the one unload event mobile Safari reliably
 * delivers. transport_type beacon so the request survives the page going away.
 */
export const trackQuizAbandon = (params: {
  step: string;
  questionIndex?: number;
  questionsAnswered: number;
  totalQuestions: number;
  reachedForm: boolean;
  formFieldsFilled?: number;
  timeOnPageMs: number;
  language: string;
}) => {
  trackQuizEvent('quiz_abandon', {
    step: params.step,
    question_index: params.questionIndex,
    questions_answered: params.questionsAnswered,
    total_questions: params.totalQuestions,
    progress_percent: Math.round((params.questionsAnswered / params.totalQuestions) * 100),
    reached_form: params.reachedForm,
    form_fields_filled: params.formFieldsFilled,
    time_on_page_ms: params.timeOnPageMs,
    language: params.language,
    transport_type: 'beacon',
  });
};
